import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ApiFilterOperation,
  DetailContext,
  DetailHandle,
  EvidenceContext,
  FormAutocomplete,
  FormPanel,
  UserContext,
  eqFilterParams,
  useFormSelector,
} from '@eas/common-web';
import { useDecisionEsFilePermission } from '@modules/decision/decision-es-file-permission';
import { useDecisionPermission } from '@modules/decision/decision-permission';
import { proceedingsSubjectsLocalTreeUrl } from '@modules/dict-proceedings-subject/dict-proceedings-subject-api';
import { subjectIndexesLocalTreeUrl } from '@modules/dict-subject-index/dict-subject-index-api';
import {
  ACCESSORY_PROPOSER_SOURCE,
  CHALLENGED_ACT_SOURCE,
  LEGAL_ADVISER_SOURCE,
  PROPOSER_SOURCE,
  REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE,
  REGULATION_EUROPEAN_UNION_LAW_SOURCE,
  REGULATION_OTHER_SOURCE,
  REGULATION_SOURCE,
} from '@modules/es-file/es-file-hooked-sources';
import { getProposerFieldPermissions } from '@modules/es-file/es-file-proposer-permission';
import { useJudges } from '@modules/judge/judge-api';
import { useUserRoles } from '@modules/role/role-api';
import { AccessoryParticipantField } from '@composite/accessory-participants/accessory-participant-field';
import { ChallengedActField } from '@composite/challenged-acts/challenged-act-field';
import { DocumentField } from '@composite/documents/document-field';
import { LegalAdviserField } from '@composite/legal-advisers/legal-adviser-field';
import { ProposerField } from '@composite/proposers/proposer-field';
import { RegulationField } from '@composite/regulations/regulation-field';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { Delimeter } from '@components/form/delimeter/delimeter';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { FormLocalTree } from '@components/form/tree/form-local-tree';
import { TreeDisabledComponent } from '@components/form/tree/tree-disabled-component';
import { CourtFile, Decision, Me } from '@models';
import { EvidenceApiUrl, RegulationType, Role, UIElement } from '@enums';

const noPermission = () => false;

export function DecisionParticipantsFields() {
  const { hookedSources } = useContext(EvidenceContext);
  const { user } = useContext<UserContext<Me>>(UserContext);
  const { source, isExisting } =
    useContext<DetailHandle<Decision>>(DetailContext);
  const decisionEsFilePermission = useDecisionEsFilePermission(source?.data);
  const esFile = source?.data?.esFile as CourtFile | undefined;
  const canWriteProposers = decisionEsFilePermission.canWrite('proposers');
  const proposerPermissions = getProposerFieldPermissions({
    esFile,
    user,
    isExisting,
    baseCanWriteProposers: canWriteProposers,
  });

  return (
    <>
      <ProposerField
        disabled={!canWriteProposers}
        esFile={esFile}
        permissions={proposerPermissions}
        source={hookedSources[PROPOSER_SOURCE]?.source}
      />

      <Delimeter />

      <AccessoryParticipantField
        disabled={!decisionEsFilePermission.canWrite('accessoryParticipants')}
        esFile={esFile}
        source={hookedSources[ACCESSORY_PROPOSER_SOURCE]?.source}
      />

      <Delimeter />

      <LegalAdviserField
        disabled={!decisionEsFilePermission.canWrite('legalAdvisers')}
        esFile={esFile}
        source={hookedSources[LEGAL_ADVISER_SOURCE]?.source}
      />

      <Delimeter />

      <ChallengedActField
        disabled={!decisionEsFilePermission.canWrite('challengedActs')}
        esFile={esFile}
        source={hookedSources[CHALLENGED_ACT_SOURCE]?.source}
      />
    </>
  );
}

export function DecisionIndexesFields() {
  const { source } = useContext<DetailHandle<Decision>>(DetailContext);
  const decisionEsFilePermission = useDecisionEsFilePermission(source?.data);

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__DECISIONS__DETAIL__PANEL_TITLE__PROCEEDINGS_SUBJECTS"
            defaultMessage="Předmět řízení"
          />
        }
      >
        <FormLocalTree
          name="esFile.proceedingsSubjects"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__PROCEEDINGS_SUBJECTS"
              defaultMessage="Předmět řízení"
            />
          }
          labelOptions={{
            hide: true,
          }}
          url={proceedingsSubjectsLocalTreeUrl}
          disabled={!decisionEsFilePermission.canWrite('proceedingSubjects')}
          DisabledComponent={TreeDisabledComponent}
        />
      </FormPanel>

      <FormPanel
        label={
          <FormattedMessage
            id="ES__DECISIONS__DETAIL__PANEL_TITLE__SUBJECT_INDEXES"
            defaultMessage="Věcný rejstřík"
          />
        }
      >
        <FormLocalTree
          name="esFile.subjectIndexes"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__SUBJECT_INDEXES"
              defaultMessage="Věcný rejstřík"
            />
          }
          labelOptions={{
            hide: true,
          }}
          url={subjectIndexesLocalTreeUrl}
          disabled={!decisionEsFilePermission.canWrite('subjectIndexes')}
          DisabledComponent={TreeDisabledComponent}
        />
      </FormPanel>
    </>
  );
}

export function DecisionRegulationsFields() {
  const { hookedSources } = useContext(EvidenceContext);
  const { source } = useContext<DetailHandle<Decision>>(DetailContext);
  const { regulationTypes, regulationEntityTypes } =
    useContext(StaticDataContext);
  const decisionEsFilePermission = useDecisionEsFilePermission(source?.data);
  const { canExecute } = useDecisionPermission(source?.data);
  const esFile = source?.data?.esFile as CourtFile | undefined;

  return (
    <RegulationField
      regulationTypes={regulationTypes}
      regulationEntityTypes={regulationEntityTypes}
      disabled={
        !canExecute(UIElement.Decision.EDIT_DECISION_BUTTON) ||
        !decisionEsFilePermission.canWrite('regulations')
      }
      esFile={esFile}
      source={hookedSources[REGULATION_SOURCE]?.source}
      typeSources={{
        [RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES]:
          hookedSources[
            REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE
          ]?.source,
        [RegulationType.EUROPEAN_UNION_LAW]:
          hookedSources[REGULATION_EUROPEAN_UNION_LAW_SOURCE]?.source,
        [RegulationType.OTHER]: hookedSources[REGULATION_OTHER_SOURCE]?.source,
      }}
    />
  );
}

export function DecisionDocumentsFields() {
  const {
    documentTypes,
    documentDeliveryMethods,
    documentOutgoingDispatchStates,
  } = useContext(StaticDataContext);

  return (
    <DocumentField
      documentTypes={documentTypes}
      documentDeliveryMethods={documentDeliveryMethods}
      documentDispatchStates={documentOutgoingDispatchStates}
      hasPermission={noPermission}
    />
  );
}

export function DecisionJudgesAndProcessorsFields() {
  const { source } = useContext<DetailHandle<Decision>>(DetailContext);
  const decisionEsFilePermission = useDecisionEsFilePermission(source?.data);

  const { judge } = useFormSelector((data: Decision) => ({
    judge: (data.esFile as CourtFile | undefined)?.judge,
  }));

  const judges = useJudges(undefined, EvidenceApiUrl.JUDGES);
  const secretaries = useUserRoles(
    eqFilterParams({ field: 'role.id', value: Role.SECRETARY })
  );
  const indexes = useUserRoles(
    eqFilterParams({ field: 'role.id', value: Role.INDEX })
  );
  const assistants = useUserRoles({
    filters: [
      {
        field: 'id',
        operation: ApiFilterOperation.IDS,
        ids: (judge?.assistantUserRoles ?? []).map((role) => role?.id),
      },
    ],
  });
  const analysts = useUserRoles(
    eqFilterParams({ field: 'role.id', value: Role.ANALYST })
  );

  if (!source.data?.esFile) {
    return null;
  }

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__DECISIONS__DETAIL__PROCESSORS_PANEL_TITLE"
            defaultMessage="Zpracovatelé spisu"
          />
        }
      >
        <FormAutocomplete
          name="esFile.judge"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__FILE_JUDGE"
              defaultMessage="Soudce zpravodaj"
            />
          }
          source={judges}
          labelMapper={autocompleteLabelMapper}
          disabled={true}
        />
        <FormAutocomplete
          name="esFile.indexUserRole"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__INDEX"
              defaultMessage="Rejstřík"
            />
          }
          source={indexes}
          labelMapper={autocompleteLabelMapper}
          disabled={true}
        />
        <FormAutocomplete
          name="esFile.secretaryUserRole"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__SECRETARY"
              defaultMessage="Tajemnice"
            />
          }
          source={secretaries}
          labelMapper={autocompleteLabelMapper}
          disabled={true}
        />
        <FormAutocomplete
          name="esFile.assistantUserRoles"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__ASSISTANTS"
              defaultMessage="Asistent"
            />
          }
          source={assistants}
          labelMapper={autocompleteLabelMapper}
          multiple
          disabled={true}
        />
        <FormAutocomplete
          name="esFile.analystUserRole"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__ANALYST"
              defaultMessage="Analytik - indexace"
            />
          }
          source={analysts}
          labelMapper={autocompleteLabelMapper}
          disabled={!decisionEsFilePermission.canWrite('analystUserRole')}
        />
      </FormPanel>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__DECISIONS__DETAIL__DECIDING_AUTHORITY_MEMBERS_PANEL_TITLE"
            defaultMessage="Rozhodující orgán"
          />
        }
      >
        <FormAutocomplete
          name="esFile.decidingAuthorityMembers"
          label={
            <FormattedMessage
              id="ES__DECISIONS__DETAIL__FIELD_LABEL__DECIDING_AUTHORITY_MEMBERS"
              defaultMessage="Členové rozhodujícího orgánu"
            />
          }
          multiple
          source={judges}
          labelMapper={autocompleteLabelMapper}
          disabled={true}
          // disabled={!decisionEsFilePermission.canWrite('decidingAuthority')}
        />
      </FormPanel>
    </>
  );
}
